import React, { Fragment } from 'react';

import { dateTimeFormatter, hockeyFormatter, statFormatter, stringFormatter } from '@nhl/formatter-utilities';
import PlayerLink from '@nhl/nhl-react-ui/lib/PlayerLink';

import GlobalColumnFunctions from '~/utils/globalColumnFunctions';
import { sumCount } from '~/views/components/records/recordTable/utils/getColumnDataForPage/columnUtils';
import columnDefs from '~/views/pages/standings/columnDefinitions';

import BaseColumnDefinitions from '../baseColumnDefinitions';

import { teamComebackWinsColumnDefinitions } from './teamComebackWinsColumnDefinitions';
import { teamInternationalColumns } from './teamInternationalColumns';
import { teamPointStreakColumnDefinitions } from './teamPointStreakColumnDefinitions';

// Shortening the function name
const CreateHeaderCell = BaseColumnDefinitions.CreateHeaderCell;
const createCell = (accessor, header, headerTitle = '') => ({
    accessor,
    getProps: GlobalColumnFunctions.getProps,
    Header: headerTitle ? CreateHeaderCell(headerTitle, header) : CreateHeaderCell(header),
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
});

//TODO: Remove this and use the ordinalFormatter from @nhl/formatter-utilities when we upgrade to the latest version
const localizedOrdinals = {
    cs: (number) => `${number}.`,
    de: (number) => `${number}.`,
    en: (number) => {
        var b = number % 10;
        var output = ~~((number % 100) / 10) === 1 ? 'th' : b === 1 ? 'st' : b === 2 ? 'nd' : b === 3 ? 'rd' : 'th';
        return number + output;
    },
    es: (number) => `${number}`,
    fi: (number) => `${number}. ERÄ`,
    fr: (number) => number + (number === 1 ? 'er' : 'e'),
    ru: (number) => `${number}-Й`,
    sk: (number) => `${number}.`,
    sv: (number) => `${number}:A`,
};

const ordinalFormatter = (number, locale = 'en') =>
    localizedOrdinals[locale] ? localizedOrdinals[locale](number) : localizedOrdinals.en(number);

// create goal string for fastest goals
const playerGoalFormatter = (string, active) => {
    const splitString = string.split(' - ');
    const player = splitString[0];
    const timeAndPeriod = splitString[1];

    return (
        <span>
            {active ? <strong title="Active Player">{player}</strong> : player} - {timeAndPeriod}
        </span>
    );
};

// Most Performances month formatter
const formatMonthDisplay = (value) => {
    const month = value && value.slice(0, 3);
    const year = value && value.slice(6, 10);
    const gp = value && value.split('-')[1];

    return (
        <Fragment>
            <div>{`${month}, ${year}`}</div>
            <div>{gp}</div>
        </Fragment>
    );
};

// Is a Round Robin Shootout game
const isRoundRobinShootoutGame = (gameId, seriesGameNumber, overtimePeriod) =>
    hockeyFormatter.isRoundRobinPlayoffGame(gameId) && !seriesGameNumber && overtimePeriod === 2;

const sortWinPctg = (a, b, homeRoad) => {
    let getWinPctg;
    if (homeRoad === 'road') {
        getWinPctg = ({ roadWins, roadLosses, roadTies }) => roadWins / (roadWins + roadLosses + roadTies);
    } else if (homeRoad === 'home') {
        getWinPctg = ({ homeWins, homeLosses, homeTies }) => homeWins / (homeWins + homeLosses + homeTies);
    } else {
        getWinPctg = ({ wins, losses, ties }) => wins / (wins + losses + ties);
    }

    return getWinPctg(a.original) - getWinPctg(b.original);
};

const getWinPctg = (wins, losses, ties?) => {
    return wins / (wins + losses + (ties ? ties : 0));
};

const getWinLossAndPctg = (wins, losses, ties?) => {
    const winPctg = statFormatter.displayValueAs('winPctg')(wins / (wins + losses + (ties ? ties : 0)));
    return wins + '-' + losses + (ties > 0 ? '-' + ties + '*' : '') + ' (' + winPctg + ')';
};

enum HomeRoad {
    HOME = 'Home',
    ROAD = 'Road',
    NONE = '',
}

// Columns -- listed alphabetically
const ALT_MATCHUP_COL = {
    accessor: 'game',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 150 : 130,
    Header: CreateHeaderCell('Game'),
    Cell: ({ original }) => (
        <div className="alt-matchup--cell">
            <div>{original.visitingTeamName}</div>
            <div>at</div>
            <div>{original.homeTeamName}</div>
        </div>
    ),
};
const FRANCHISE_COL = {
    accessor: 'franchiseName',
    Cell: ({ original, value }) => (
        <Fragment>
            {value}
            {original.activeStreak && <span title="Streak is Active" className="active-indicator-icon" />}
        </Fragment>
    ),
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 150 : 130,
    headerClassName: 'left',
    className: 'left',
    Header: CreateHeaderCell('Franchise', 'Franchise', { textAlign: 'left' }),
};

const OPPONENT_FRANCHISE_COL = {
    accessor: 'opponentName',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 150 : 130,
    headerClassName: 'left',
    className: 'left',
    Header: CreateHeaderCell('Opponent', 'Opponent', { textAlign: 'left' }),
};

const GAME_BOTH_TEAMS_COL = {
    accessor: 'gameId',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Game'),
    Cell: ({ original }) => {
        const { gameId, homeTeamName, visitingTeamName } = original;
        return (
            <div>
                <a href={`https://www.nhl.com/gamecenter/${gameId}`}>
                    {visitingTeamName}
                    <div>at</div>
                    {homeTeamName}
                </a>
            </div>
        );
    },
};
const GAME_BOTH_TEAMS_NO_LINK_COL = {
    accessor: 'gameId',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Matchup'),
    Cell: ({ original }) => {
        const { homeTeamName, visitingTeamName } = original;
        return (
            <div>
                {visitingTeamName}
                <div>at</div>
                {homeTeamName}
            </div>
        );
    },
};
const GAMES_PLAYED_BY_ROUND = {
    accessor: 'gamesPlayedByRound',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Games Played by Round', 'GP by Round'),
};
const GOAL_1_COL = {
    accessor: 'goal1',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer1),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 1', 'G1'),
};
const GOAL_2_COL = {
    accessor: 'goal2',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer2),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 2', 'G2'),
};
const GOAL_3_COL = {
    accessor: 'goal3',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer3),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 3', 'G3'),
};
const GOAL_4_COL = {
    accessor: 'goal4',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer4),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 4', 'G4'),
};
const GOAL_5_COL = {
    accessor: 'goal5',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer5),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 5', 'G5'),
};
const GOAL_6_COL = {
    accessor: 'goal6',
    Cell: ({ original, value }) => value && playerGoalFormatter(value, original.activePlayer6),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 6', 'G6'),
};
const GOALS_AGAINST_PER_GAME = {
    accessor: 'goalsAgainstPerGame',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => statFormatter.displayValueAs('goalsAgainstPerGame')(value),
    Header: CreateHeaderCell('Goals Against Per Game', 'GA/GP'),
    sortType: 'basic',
};
const GOALS_PER_GAME_COL = {
    accessor: 'goalsPerGame',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => statFormatter.displayValueAs('goalsPerGame')(value),
    Header: CreateHeaderCell('Goals Per Game', 'G/GP'),
    sortType: 'basic',
};
const HOME_LOSSES = {
    accessor: 'homeLosses',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Home Losses', 'HL'),
};
const HOME_WINS = {
    accessor: 'homeWins',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Home Wins', 'HW'),
};
const POINT_PCTG_COL = {
    accessor: 'pointPctg',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) =>
        value > 1
            ? statFormatter.displayValueAs('pointPctg')(value / 100)
            : statFormatter.displayValueAs('pointPctg')(value),
    Header: CreateHeaderCell('Point %', 'P%'),
    sortType: 'basic',
};
const PPG_COL = {
    accessor: 'powerPlayGoals',
    Header: CreateHeaderCell('Power Play Goals For', 'PPG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const HOME_PPG_COL = {
    accessor: 'homePowerPlayGoals',
    Header: CreateHeaderCell('Home Power Play Goals For', 'Home PPG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const ROAD_PPG_COL = {
    accessor: 'roadPowerPlayGoals',
    Header: CreateHeaderCell('Road Power Play Goals For', 'Road PPG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const HOME_SHG_COL = {
    accessor: 'homeShorthandedGoals',
    Header: CreateHeaderCell('Home Shorthanded Goals For', 'Home SHG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const ROAD_SHG_COL = {
    accessor: 'roadShorthandedGoals',
    Header: CreateHeaderCell('Road Shorthanded Goals For', 'Road SHG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const PPGA = {
    accessor: 'powerPlayGoalsAgainst',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Power Play Goals Against', 'PPGA'),
};
const RESULT_COL = {
    accessor: 'result',
    Cell: ({ value }) => value || '--',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Result'),
};
const ROAD_LOSSES = {
    accessor: 'roadLosses',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Road Losses', 'RL'),
};
const ROAD_WINS = {
    accessor: 'roadWins',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Road Wins', 'RW'),
};
const SECONDS_SINCE_LAST_GOAL_COL = {
    accessor: 'secondsSinceLastGoal',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 50,
    Header: CreateHeaderCell('Seconds Since Last Goal', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
};
const SECONDS_SINCE_LAST_TWO_GOALS_COL = {
    accessor: 'secondsSinceLast2Goals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seconds Since Last 2 Goals', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
};
const SECONDS_SINCE_LAST_THREE_GOALS_COL = {
    accessor: 'secondsSinceLast3Goals',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    Header: CreateHeaderCell('Seconds Since Last 3 Goals', 'Time'),
};
const SECONDS_SINCE_LAST_FOUR_GOALS_COL = {
    accessor: 'secondsSinceLast4Goals',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    Header: CreateHeaderCell('Seconds Since Last 4 Goals', 'Time'),
};
const SECONDS_SINCE_LAST_FIVE_GOALS_COL = {
    accessor: 'secondsSinceLast5Goals',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    Header: CreateHeaderCell('Seconds Since Last 5 Goals', 'Time'),
};
const SHG_COL = {
    accessor: 'shorthandedGoals',
    Header: CreateHeaderCell('Shorthanded Goals', 'SHG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const SHGA = {
    accessor: 'shorthandedGoalsAgainst',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shorthanded Goals Against', 'SHGA'),
};
const SHOTS_GENERAL = createCell('shots', 'Shots');
const SHOTS_OT_1 = createCell('shotsOt1', 'OT1 S', 'Overtime 1 Shots');
const SHOTS_OT_2 = createCell('shotsOt2', 'OT2 S', 'Overtime 2 Shots');
const SHOTS_OT_3 = createCell('shotsOt3', 'OT3 S', 'Overtime 3 Shots');
const SHOTS_OT_4 = createCell('shotsOt4', 'OT4 S', 'Overtime 4 Shots');
const SHOTS_OT_5 = createCell('shotsOt5', 'OT5 S', 'Overtime 5 Shots');
const SHOTS_OT_TOTAL = createCell('shots', 'Total OT S', 'Total Overtime Shots');
const STANLEY_CUP_APPEARANCES = {
    accessor: 'stanleyCupAppearances',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Appearances', 'Appearances'),
};
const STANLEY_CUP_WINNER = {
    accessor: 'stanleyCupWinner',
    Cell: ({ original, value }) => (
        <Fragment>
            {value ? value : <span title="Stanley Cup Not Yet Awarded">-</span>}
            {original.activeStreak && <span title="Streak is Active" className="active-indicator-icon" />}
        </Fragment>
    ),
    headerClassName: 'left',
    className: 'left',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Winner'),
};
const STANLEY_CUP_WINS = {
    accessor: 'stanleyCupWins',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Wins', 'Wins'),
};
const CUPS_COL = {
    accessor: 'cups',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Wins', 'Stanley Cups'),
};
const SCF_SERIES_COL = {
    accessor: 'seriesPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Appearances', 'Stanley Cup Final Appearances'),
};
const SCF_CONSEC_APPEARANCES_COL = {
    accessor: 'consecutiveSeasons',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Appearances', 'Consecutive Stanley Cup Final Appearances'),
};
const SCF_SERIES_RECORD_COL = {
    Header: CreateHeaderCell('Series Record'),
    accessor: 'seriesWins',
    Cell: ({ row }) => getWinLossAndPctg(row.original.seriesWins, row.original.seriesLosses),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        return (
            getWinPctg(a.original.totalSeriesWins, a.original.totalSeriesLosses) -
            getWinPctg(b.original.totalSeriesWins, b.original.totalSeriesLosses)
        );
    },
    sortDescFirst: true,
};
const SCF_RESULT_COL = {
    Header: CreateHeaderCell('Result'),
    accessor: 'seriesWinner',
    Cell: ({ row }) => {
        const { seasonId, teamAbbrev, opponentTeamAbbrev, wins, losses, ties, seriesWinner, seriesLength } =
            row.original;
        if (seasonId === 19181919) {
            return teamAbbrev + ' ties ' + opponentTeamAbbrev + ' ' + wins + '-' + losses + '-' + ties;
        }
        if (wins + losses + ties !== seriesLength) return '--';
        return `${teamAbbrev} ${seriesWinner ? 'wins' : 'loses'} ${wins}-${losses}${ties > 0 ? '-' + ties : ''}`;
    },
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};
const TEAM_GOALS_SPLIT_COL = {
    accessor: 'goalTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal Team Totals'),
};
const TEAM_PPG_SPLIT_COL = {
    accessor: 'powerPlayGoalTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Power-Play Goal Team Totals', 'PPG Team Totals'),
};
const TEAM_SHG_SPLIT_COL = {
    accessor: 'shorthandedGoalTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shorthanded Goal Team Totals', 'SHG Team Totals'),
};
const TEAM_SHOTS_SPLIT_COL = {
    accessor: 'shotTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shot Team Totals', 'Breakdown'),
};
const TEAM_SHUTOUTS_SPLIT_COL = {
    accessor: 'shutoutTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shutout Team Totals'),
};
const TEAM_PENALTY_SPLIT_COL = {
    accessor: 'penaltyTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Penalty Team Totals', 'PEN Team Totals'),
};
const TEAM_PIM_SPLIT_COL = {
    accessor: 'pimTeamTotals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Penalty Minute Team Totals', 'PIM Team Totals'),
};
const HOME_SHOTS_COL = {
    accessor: 'homeShots',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Home Shots'),
};
const VISITING_SHOTS_COL = {
    accessor: 'visitingShots',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Vis. Shots'),
};
const HOME_HITS_COL = {
    accessor: 'homeHits',
    Header: CreateHeaderCell('Home Hits'),
    getProps: GlobalColumnFunctions.getProps,
};
const VISITING_HITS_COL = {
    accessor: 'visitingHits',
    Header: CreateHeaderCell('Vis. Hits'),
    getProps: GlobalColumnFunctions.getProps,
};
const HOME_BLOCKS_COL = {
    accessor: 'homeBlocks',
    Header: CreateHeaderCell('Home Blocks'),
    getProps: GlobalColumnFunctions.getProps,
};
const HOME_PENALTY_MINUTES_COL = {
    accessor: 'homePenaltyMinutes',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Home Penalty Minutes', 'Home PIM'),
    sortDescFirst: true,
};

const ROAD_SHOTS_COL = {
    accessor: 'roadShots',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Road Shots'),
};
const ROAD_HITS_COL = {
    accessor: 'roadHits',
    Header: CreateHeaderCell('Road Hits'),
    getProps: GlobalColumnFunctions.getProps,
};
const ROAD_BLOCKS_COL = {
    accessor: 'roadBlocks',
    Header: CreateHeaderCell('Road Blocks'),
    getProps: GlobalColumnFunctions.getProps,
};
const ROAD_PENALTY_MINUTES_COL = {
    accessor: 'roadPenaltyMinutes',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Road Penalty Minutes', 'Road PIM'),
    sortDescFirst: true,
};
const WIN_PCTG_COL = {
    accessor: 'winPctg',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Win Percentage', 'W%'),
    Cell: ({ value }) => statFormatter.displayValueAs('pointPctg')(value),
    sortType: 'basic',
};
const GOALIE_COL = {
    Header: 'Goaltender(s)',
    accessor: 'goalie',
    getProps: GlobalColumnFunctions.getProps,
};
const SERIES_GAME_RECORD = {
    Header: CreateHeaderCell('Overall'),
    accessor: 'wins',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ row }) => getWinLossAndPctg(row.original.wins, row.original.losses, row.original.ties),
    sortType: (a, b) => sortWinPctg(a, b, ''),
    sortDescFirst: true,
};
const SERIES_HOME_GAME_RECORD = {
    Header: CreateHeaderCell('Home'),
    accessor: 'homeWins',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ row }) => getWinLossAndPctg(row.original.homeWins, row.original.homeLosses, row.original.homeTies),
    sortType: (a, b) => sortWinPctg(a, b, 'home'),
    sortDescFirst: true,
};
const SERIES_ROAD_GAME_RECORD = {
    Header: CreateHeaderCell('Road'),
    accessor: 'RoadWins',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ row }) => getWinLossAndPctg(row.original.roadWins, row.original.roadLosses, row.original.roadTies),
    sortType: (a, b) => sortWinPctg(a, b, 'road'),
    sortDescFirst: true,
};
const MATCHUP_COL = {
    accessor: 'matchup',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Matchup'),
    Cell: ({ value }) => {
        if (!value) {
            return;
        }
        const teams = value.split(' vs ');
        return (
            <div>
                {teams[0]}
                <div>vs</div>
                {teams[1]}
            </div>
        );
    },
};
const SERIES_RECORD_COL = {
    Header: CreateHeaderCell('Total Series Record'),
    accessor: 'totalSeriesWins',
    Cell: ({ row }) => getWinLossAndPctg(row.original.totalSeriesWins, row.original.totalSeriesLosses),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        return (
            getWinPctg(a.original.totalSeriesWins, a.original.totalSeriesLosses) -
            getWinPctg(b.original.totalSeriesWins, b.original.totalSeriesLosses)
        );
    },
    sortDescFirst: true,
};
const HOME_WINS_COL = {
    Header: CreateHeaderCell('Starting At Home'),
    accessor: 'seriesStartingHomeWins',
    Cell: ({ row }) => getWinLossAndPctg(row.original.seriesStartingHomeWins, row.original.seriesStartingHomeLosses),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        return (
            getWinPctg(a.original.seriesStartingHomeWins, a.original.seriesStartingHomeLosses) -
            getWinPctg(b.original.seriesStartingHomeWins, b.original.seriesStartingHomeLosses)
        );
    },
    sortDescFirst: true,
};
const ROAD_WINS_COL = {
    Header: CreateHeaderCell('Starting on Road'),
    accessor: 'seriesStartingRoadWins',
    Cell: ({ row }) => getWinLossAndPctg(row.original.seriesStartingRoadWins, row.original.seriesStartingRoadLosses),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        return (
            getWinPctg(a.original.seriesStartingRoadWins, a.original.seriesStartingRoadLosses) -
            getWinPctg(b.original.seriesStartingRoadWins, b.original.seriesStartingRoadLosses)
        );
    },
    sortDescFirst: true,
};
const GWG_COL = {
    accessor: 'gameWinningGoalPlayerLastName',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 150 : 130,
    headerClassName: 'left',
    className: 'left',
    Header: CreateHeaderCell('Game Winning Goal Player', 'GWG', { textAlign: 'left' }),
    Cell: ({ original }) => {
        if (!original.gameWinningGoalPlayerId) {
            return 'N/A';
        }
        const playerObj = {
            playerId: original.gameWinningGoalPlayerId,
            firstName: original.gameWinningGoalPlayerFirstName,
            lastName: original.gameWinningGoalPlayerLastName,
        };
        return <PlayerLink player={playerObj} />;
    },
};
const OT_SECONDS_COL = {
    accessor: 'overtimeSeconds',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    Header: CreateHeaderCell('Time in Overtime', 'OT Length'),
};
const OT_PERIODS_COL = {
    accessor: 'overtimePeriods',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Overtime Periods', 'OT Period(s)'),
};
const OT_GP_COL = {
    accessor: 'overtimeGamesPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Overtime Games Played', 'OT GP'),
};
const OT_GAMES_PLAYED_COL = (homeRoad: HomeRoad) => ({
    accessor: `overtime${homeRoad}GamesPlayed`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell(`Overtime ${homeRoad} Games Played`, `${homeRoad} OT GP`),
});
const OT_WINS_COL = (homeRoad: HomeRoad) => ({
    accessor: `overtime${homeRoad}Wins`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell(`${homeRoad} Wins in Overtime`, `${homeRoad} OT W`),
});
const OT_LOSSES_COL = (homeRoad: HomeRoad) => ({
    accessor: `overtime${homeRoad}Losses`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell(`${homeRoad} Losses in Overtime`, `${homeRoad} OT L`),
});
const OT_TIES_COL = (homeRoad: HomeRoad) => ({
    accessor: `overtime${homeRoad}Ties`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell(`${homeRoad} Ties in Overtime`, `${homeRoad} OT T`),
});
const OT_WIN_PERCENTAGE_COL = (homeRoad: HomeRoad) => ({
    accessor: `overtime${homeRoad}WinPercentage`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell(`${homeRoad} Overtime Win Percentage`, `${homeRoad} OT W%`),
    Cell: ({ value }) => statFormatter.displayValueAs('winPctg')(value),
    sortType: 'basic',
});

// Common primary sorts
const getBaseColumns = (commonColumns) => [
    ...commonColumns,
    BaseColumnDefinitions.POINTS_COL,
    POINT_PCTG_COL,
    BaseColumnDefinitions.GOALS_COL,
    GOALS_PER_GAME_COL,
    BaseColumnDefinitions.GOALS_AGAINST_COL,
    GOALS_AGAINST_PER_GAME,
];
const getMonthBaseColumns = (commonColumns) => [
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...commonColumns,
    BaseColumnDefinitions.POINTS_COL,
    POINT_PCTG_COL,
    BaseColumnDefinitions.GOALS_FOR_COL,
    BaseColumnDefinitions.GOALS_AGAINST_COL,
];

// Common column groupings
const COMMON_COLS = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
    BaseColumnDefinitions.OVERTIME_LOSSES_COL,
];
const COMMON_PLAYOFF_COLS = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
];
const FACEOFFS = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.OVERTIME_LOSSES_COL,
    BaseColumnDefinitions.FACEOFFS_WON_COL,
    BaseColumnDefinitions.FACEOFFS_LOST_COL,
    BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
    BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
];
const GIVE_TAKEAWAYS = [
    PPG_COL,
    PPGA,
    POINT_PCTG_COL,
    BaseColumnDefinitions.GIVEAWAYS_COL,
    BaseColumnDefinitions.TAKEAWAYS_COL,
];
const HITS = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.OVERTIME_LOSSES_COL,
    BaseColumnDefinitions.POINTS_COL,
    BaseColumnDefinitions.HITS_COL,
    BaseColumnDefinitions.BLOCKED_SHOTS_COL,
    BaseColumnDefinitions.MISSED_SHOTS_COL,
    BaseColumnDefinitions.GIVEAWAYS_COL,
    BaseColumnDefinitions.TAKEAWAYS_COL,
];
const HOME_COMMON = [
    HOME_WINS,
    HOME_LOSSES,
    {
        accessor: 'homeTies',
        Header: CreateHeaderCell('Home Ties', 'HT'),
        getProps: GlobalColumnFunctions.getProps,
    },
];
const TEAM_TIES_COMMON = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
];
const TEAM_WL_COMMON = [...TEAM_TIES_COMMON, BaseColumnDefinitions.OVERTIME_LOSSES_COL];
const LONGEST_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    {
        accessor: 'streakLength',
        getProps: GlobalColumnFunctions.getProps,
        Header: CreateHeaderCell('Streak'),
    },
    ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
];
const FRANCHISE_LONGEST_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    FRANCHISE_COL,
    OPPONENT_FRANCHISE_COL,
    {
        accessor: 'streak',
        getProps: GlobalColumnFunctions.getProps,
        Header: CreateHeaderCell('Streak'),
    },
    ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
];
const PENALTY_COLS = [BaseColumnDefinitions.PENALTIES_COL, BaseColumnDefinitions.PENALTY_MINUTES_COL];
const POWER_PLAY = [PPG_COL, ...COMMON_COLS];
const ROAD_COMMON = [
    ROAD_WINS,
    ROAD_LOSSES,
    {
        accessor: 'roadTies',
        Header: CreateHeaderCell('Road Ties', 'RT'),
        getProps: GlobalColumnFunctions.getProps,
    },
];
const SHOOTOUT_BASE = [
    {
        accessor: 'winPctg',
        Header: CreateHeaderCell('Win %', 'W%'),
        Cell: ({ value }) => statFormatter.displayValueAs('winPctg')(value),
        getProps: GlobalColumnFunctions.getProps,
        sortType: 'basic',
    },
    BaseColumnDefinitions.GOALS_COL,
    BaseColumnDefinitions.GOALS_AGAINST_COL,
    {
        accessor: 'shots',
        Header: CreateHeaderCell('Shots For', 'SF'),
        getProps: GlobalColumnFunctions.getProps,
    },
    BaseColumnDefinitions.SHOTS_AGAINST_COL,
    {
        accessor: 'shootingPctg',
        Header: CreateHeaderCell('Shooting %', 'S%'),
        Cell: ({ value }) => statFormatter.displayValueAs('shootingPctg')(value),
        getProps: GlobalColumnFunctions.getProps,
        sortType: 'basic',
    },
    {
        accessor: 'opponentShootingPctg',
        Header: CreateHeaderCell('Opponent Shooting %', 'OPP S%'),
        Cell: ({ value }) => statFormatter.displayValueAs('shootingPctg')(value),
        getProps: GlobalColumnFunctions.getProps,
        sortType: 'basic',
    },
];
const SHOOTOUT = [BaseColumnDefinitions.WINS_COL, BaseColumnDefinitions.LOSSES_COL, ...SHOOTOUT_BASE];
const SHOOTOUT_HOME = [HOME_WINS, HOME_LOSSES, ...SHOOTOUT_BASE];
const SHOOTOUT_ROAD = [ROAD_WINS, ROAD_LOSSES, ...SHOOTOUT_BASE];
const SHORTHAND = [SHG_COL, ...COMMON_COLS];
const SHORTHAND_PLAYOFFS = [SHG_COL, ...COMMON_PLAYOFF_COLS];
const TEAM_SERIES_BOTH_TEAM_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.SEASON_COL,
    MATCHUP_COL,
    BaseColumnDefinitions.SERIES_TITLE,
    RESULT_COL,
    BaseColumnDefinitions.ROUND_NUMBER_COL,
];
const TEAM_SERIES_ONE_TEAM_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.OPPONENT_COL,
    BaseColumnDefinitions.SERIES_TITLE,
    BaseColumnDefinitions.ROUND_NUMBER_COL,
];
const TIMED_OT_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    ALT_MATCHUP_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
    OT_SECONDS_COL,
    GWG_COL,
    {
        accessor: 'homeGoalieLastName',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: window.innerWidth > 414 ? 150 : 130,
        headerClassName: 'left',
        className: 'left',
        Header: CreateHeaderCell('Home Goalie', 'H. Goalie', { textAlign: 'left' }),
        Cell: ({ original }) => {
            if (!original.homeGoaliePlayerId) {
                return 'N/A';
            }

            const playerObj = {
                playerId: original.homeGoaliePlayerId,
                firstName: original.homeGoalieFirstName,
                lastName: original.homeGoalieLastName,
            };

            return <PlayerLink player={playerObj} />;
        },
    },
    {
        accessor: 'visitingGoalieLastName',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: window.innerWidth > 414 ? 150 : 130,
        headerClassName: 'left',
        className: 'left',
        Header: CreateHeaderCell('Visiting Goalie', 'V. Goalie', { textAlign: 'left' }),
        Cell: ({ original }) => {
            if (!original.visitingGoaliePlayerId) {
                return 'N/A';
            }

            const playerObj = {
                playerId: original.visitingGoaliePlayerId,
                firstName: original.visitingGoalieFirstName,
                lastName: original.visitingGoalieLastName,
            };

            return <PlayerLink player={playerObj} />;
        },
    },
];
const PPG_COLS = [
    {
        accessor: 'homePowerPlayGoals',
        Header: CreateHeaderCell('Home Power Play Goals', 'Home PPG'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    {
        accessor: 'visitingPowerPlayGoals',
        Header: CreateHeaderCell('Road Power Play Goals', 'Road PPG'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    PPG_COL,
];

const SHG_COLS = [
    {
        accessor: 'homeShorthandedGoals',
        Header: CreateHeaderCell('Home Shorthanded Goals', 'Home SHG'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    {
        accessor: 'visitingShorthandedGoals',
        Header: CreateHeaderCell('Road Shorthanded Goals', 'Road SHG'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    SHG_COL,
];

const SERIES_SITUATION_COLS = [
    {
        Header: CreateHeaderCell('Series Situation'),
        accessor: 'seriesSituation',
        getProps: GlobalColumnFunctions.getProps,
        sortType: (a, b) => {
            const val1 = a.original;
            const val2 = b.original;
            if (val1.seriesLosses === 0 || val2.seriesLosses === 0) {
                return val1.seriesLosses === val2.seriesLosses
                    ? val1.seriesWins < val2.seriesWins
                        ? 1
                        : -1
                    : val1.seriesLosses === 0
                    ? 1
                    : -1;
            }
            if (val1.seriesWins === 0 || val2.seriesWins === 0) {
                return val1.seriesWins === val2.seriesWins
                    ? val1.seriesLosses < val2.seriesLosses
                        ? 1
                        : -1
                    : val1.seriesWins === 0
                    ? 1
                    : -1;
            }
            if (val1.seriesWins === val1.seriesLosses || val2.seriesWins === val2.seriesLosses) {
                return val1.seriesWins === val1.seriesLosses && val2.seriesWins === val2.seriesLosses
                    ? val1.seriesWins < val2.seriesLosses
                        ? 1
                        : -1
                    : val1.seriesWins === val1.seriesLosses
                    ? 1
                    : -1;
            }
            if (val1.seriesLosses === 1 || val2.seriesLosses === 1) {
                return val1.seriesLosses === val2.seriesLosses
                    ? val1.seriesWins < val2.seriesWins
                        ? 1
                        : -1
                    : val1.seriesLosses === 1
                    ? 1
                    : -1;
            }
            if (val1.seriesWins === 1 || val2.seriesWins === 1) {
                return val1.seriesWins === val2.seriesWins
                    ? val1.seriesLosses < val2.seriesLosses
                        ? 1
                        : -1
                    : val1.seriesWins === 1
                    ? 1
                    : -1;
            } else return 0;
        },
    },
    SERIES_RECORD_COL,
    HOME_WINS_COL,
    ROAD_WINS_COL,
];
const GOALS_FOR_GOALS_AGAINST_COLS = [
    { ...BaseColumnDefinitions.GOALS_FOR_COL, accessor: 'goalsFor' },
    BaseColumnDefinitions.GOALS_AGAINST_COL,
];
const ELIMINATION_GAMES_COLS = [
    columnDefs.FRANCHISE_ID_COL('lastSeason'),
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
    {
        ...WIN_PCTG_COL,
        aggregate: (vals, rows) => {
            const wins = sumCount(rows.map((row) => row.wins));
            const gamesPlayed = sumCount(rows.map((row) => row.gamesPlayed));
            return wins / gamesPlayed;
        },
    },
    ...GOALS_FOR_GOALS_AGAINST_COLS,
];
const ELIMINATION_GAMES_SEASON_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    ...GOALS_FOR_GOALS_AGAINST_COLS,
];
const ELIMINATION_GAMES_SERIES_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.ROUND_NUMBER_COL,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    ...GOALS_FOR_GOALS_AGAINST_COLS,
];
const ELIMINATION_GAME_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SERIES_TITLE,
    { ...BaseColumnDefinitions.SERIES_GAME_NUMBER_COL, Header: 'Game # of Series' },
];
const OUTDOOR_GAME_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.OPPONENT_COL,
    BaseColumnDefinitions.EVENT_NAME_COL,
    BaseColumnDefinitions.GAME_COL,
];
const OUTDOOR_DATE_SCORE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.EVENT_NAME_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
];
const TEAM_OPP_SEASON_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.OPPONENT_COL,
    BaseColumnDefinitions.SEASON_COL,
];
const TEAM_OPP_SEASON_DATE_COLS = [...TEAM_OPP_SEASON_COLS, BaseColumnDefinitions.GAME_DATE_COL];
const TEAM_SEASON_DATE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
];
const SCF_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    FRANCHISE_COL,
    { ...BaseColumnDefinitions.PLAYOFF_SEASONS_COL, Header: 'Playoff Appearances' },
    SCF_SERIES_COL,
    CUPS_COL,
];
const SCF_GAME_NUM_RESULT_COLS = [
    { ...BaseColumnDefinitions.SERIES_GAME_NUMBER_COL, Header: 'Game #' },
    { ...BaseColumnDefinitions.SCORE_COL, Header: 'Result' },
];
const SCF_BOTH_TEAMS_GOALS_BASE = [
    BaseColumnDefinitions.INDEX_COL,
    GAME_BOTH_TEAMS_NO_LINK_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
    ...SCF_GAME_NUM_RESULT_COLS,
];
const SCF_SERIES_BOTH_TEAMS_BASE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    MATCHUP_COL,
    BaseColumnDefinitions.SEASON_COL,
    RESULT_COL,
    { ...BaseColumnDefinitions.GAMES_PLAYED_COL, accessor: 'seriesLength' },
];
const OT_STATS_COLS = (homeRoad: HomeRoad) => [
    OT_GAMES_PLAYED_COL(homeRoad),
    OT_WINS_COL(homeRoad),
    OT_LOSSES_COL(homeRoad),
    OT_TIES_COL(homeRoad),
    OT_WIN_PERCENTAGE_COL(homeRoad),
];

export const teamColumnDefinitions = {
    // Games Played - playoff
    'most-games-played-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        GAMES_PLAYED_BY_ROUND,
    ],
    'most-games-played-one-team-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS],

    // Standings
    'team-most-points-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'best-points-pctg-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-points-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-points-season-70-game-min': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'worst-points-pctg-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],

    // Wins
    'team-most-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'most-home-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'most-road-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'fewest-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-wins-one-season-70-game-min': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-road-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'fewest-road-wins-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(TEAM_WL_COMMON),
    ],
    'fewest-home-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'fewest-home-wins-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(TEAM_WL_COMMON),
    ],

    // Wins - playoffs
    'most-home-wins-all-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        HOME_WINS,
    ],
    'most-home-wins-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...HOME_COMMON],
    'most-road-wins-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...ROAD_COMMON],
    'most-road-wins-all-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ROAD_WINS,
    ],
    'most-ot-home-wins-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_MONTH_COLS,
        {
            ...BaseColumnDefinitions.HOME_GAMES_PLAYED_COL,
            Header: CreateHeaderCell('Home Games Played', 'Home GP'),
        },
        ...OT_STATS_COLS(HomeRoad.HOME),
    ],
    'most-ot-road-wins-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_MONTH_COLS,
        {
            ...BaseColumnDefinitions.ROAD_GAMES_PLAYED_COL,
            Header: CreateHeaderCell('Road Games Played', 'Road GP'),
        },
        ...OT_STATS_COLS(HomeRoad.ROAD),
    ],
    'most-ot-wins-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...OT_STATS_COLS(HomeRoad.NONE)],

    // Losses
    'fewest-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-losses-one-season-70-game-min': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-home-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'fewest-home-losses-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(TEAM_WL_COMMON),
    ],
    'fewest-road-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'fewest-road-losses-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(TEAM_WL_COMMON),
    ],
    'team-most-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'most-home-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],
    'most-road-losses-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_WL_COMMON)],

    // Losses - playoff
    'most-losses-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...COMMON_PLAYOFF_COLS],
    'most-ot-losses-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...HOME_COMMON,
        {
            accessor: 'playoffLossesInOvertime',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Playoff Losses in Overtime', 'OTL'),
        },
    ],
    'most-home-losses-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...HOME_COMMON],
    'most-ot-home-losses-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...HOME_COMMON,
        {
            accessor: 'playoffHomeLossesInOvertime',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Playoff Home Losses in Overtime', 'OTL'),
        },
    ],
    'most-road-losses-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...ROAD_COMMON],
    'most-ot-road-losses-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...ROAD_COMMON,
        {
            accessor: 'playoffRoadLossesInOvertime',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Playoff Road Losses in Overtime', 'OTL'),
        },
    ],

    // Ties
    'team-most-ties-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'most-home-ties-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_TIES_COMMON)],
    'most-road-ties-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(TEAM_TIES_COMMON)],
    'fewest-ties-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-ties-one-season-70-game-min': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],

    // Goals
    'team-most-goals-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'most-goals-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.SCORE_COL,
    ],
    'most-consecutive-goals-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        {
            accessor: 'consecutiveGoals',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Goals', 'Goals'),
        },
    ],
    'most-goals-both-teams-one-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-one-team-one-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'fewest-goals-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-goals-one-season-70-game-min': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'fewest-goals-against-per-game-average-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(COMMON_COLS),
    ],
    'highest-goals-per-game-avg': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'lowest-goals-per-game-avg': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],

    // Goals - playoffs
    'most-goals-both-teams-one-game-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GOALS_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-goals-one-team-one-game-playoff': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GOALS_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-goals-both-teams-one-period-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-one-team-one-period-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],

    // Power play goals
    'team-most-power-play-goals-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...POWER_PLAY],
    'team-most-power-play-goals-one-team-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        PPG_COL,
    ],
    'team-most-power-play-goals-both-teams-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...PPG_COLS,
    ],
    'team-most-power-play-goals-both-teams-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        PPG_COL,
    ],
    'team-most-power-play-goals-one-team-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        PPG_COL,
    ],
    'most-power-play-goals-against-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        PPG_COL,
        PPGA,
    ],

    // Power play goals - playoffs
    'most-ppg-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        PPG_COL,
    ],
    'most-ppg-one-team-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, PPG_COL, PPGA],
    'most-ppg-both-teams-one-series-playoff': [...TEAM_SERIES_BOTH_TEAM_COLS, PPG_COL, TEAM_PPG_SPLIT_COL],
    'most-ppg-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, PPG_COL],
    'most-ppg-one-team-one-game-playoff': [...TEAM_OPP_SEASON_DATE_COLS, BaseColumnDefinitions.SCORE_COL, PPG_COL],
    'most-ppg-one-team-one-period-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        PPG_COL,
    ],
    'most-ppg-both-teams-one-period-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        PPG_COL,
        TEAM_PPG_SPLIT_COL,
    ],
    'most-ppg-both-teams-one-game-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...PPG_COLS,
    ],

    // Shorthanded goals
    'most-shorthanded-goals-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHORTHAND],
    'team-most-shorthanded-goals-one-team-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SHG_COL,
    ],
    'team-most-shorthanded-goals-both-teams-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...SHG_COLS,
    ],
    'team-most-shorthanded-goals-both-teams-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHG_COL,
    ],
    'team-most-shorthanded-goals-one-team-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHG_COL,
    ],
    'most-shorthanded-goals-against-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SHG_COL,
        SHGA,
    ],

    // Shorthanded goals - playoffs
    'most-shg-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        SHG_COL,
    ],
    'most-shg-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, SHG_COL],
    'most-shg-both-teams-one-series-playoff': [...TEAM_SERIES_BOTH_TEAM_COLS, SHG_COL, TEAM_SHG_SPLIT_COL],
    'most-shg-one-team-one-season-playoff': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHORTHAND_PLAYOFFS],
    'most-shg-one-team-one-game-playoff': [...TEAM_OPP_SEASON_DATE_COLS, BaseColumnDefinitions.SCORE_COL, SHG_COL],
    'most-shg-one-team-one-period-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHG_COL,
    ],
    'most-shg-both-teams-one-period-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHG_COL,
        TEAM_SHG_SPLIT_COL,
    ],
    'most-shg-both-teams-one-game-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...SHG_COLS,
    ],

    // Fewest goals - playoffs
    'fewest-goals-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-2-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-3-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-4-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-5-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-6-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-one-team-7-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'fewest-goals-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-2-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-3-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-4-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-5-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-6-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'fewest-goals-both-teams-7-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],

    // Most goals - playoffs
    'most-goals-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-2-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-3-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-4-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-5-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-6-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-team-7-game-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-2-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-3-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-4-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-5-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-6-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],
    'most-goals-both-teams-7-game-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        TEAM_GOALS_SPLIT_COL,
    ],

    // 50-40-30-20 Goal scorers
    'most-30-plus-goals-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'thirtyGoalScorers',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Thirty Goal Scorers'),
        },
        createCell('thirtyGoalScorerNames', 'Players'),
    ],
    'most-20-plus-goals-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'twentyGoalScorers',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Twenty Goal Scorers'),
        },
        createCell('twentyGoalScorerNames', 'Players'),
    ],
    'most-40-plus-goals-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'fortyGoalScorers',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Forty Goal Scorers'),
        },
        createCell('fortyGoalScorerNames', 'Players'),
    ],
    'most-50-plus-goals-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'fiftyGoalScorers',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Fifty Goal Scorers'),
        },
        createCell('fiftyGoalScorerNames', 'Players'),
    ],

    // 100 Point scorers
    'most-100-plus-points-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'oneHundredPointScorers',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('100 Point Scorers'),
        },
        createCell('oneHundredPointScorerNames', 'Players'),
    ],

    // Scoring
    'most-points-one-team-one-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.SCORING_POINTS_COL,
    ],
    'most-scoring-points-both-teams-one-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.SCORING_POINTS_COL,
    ],

    // Scoring - playoff
    'most-scoring-points-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        BaseColumnDefinitions.SCORING_POINTS_COL,
    ],

    // Goals Against
    'fewest-goals-against-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(COMMON_COLS),
    ],
    'fewest-goals-against-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],
    'highest-goals-against-per-game-average-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...getBaseColumns(COMMON_COLS),
    ],
    'most-goals-against-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...getBaseColumns(COMMON_COLS)],

    // Hat tricks - playoffs
    'most-hat-tricks-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        {
            accessor: 'hatTricks',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Hat Tricks'),
        },
        {
            accessor: 'hatTrickTeamTotals',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Hat Trick Team Totals'),
        },
    ],
    'most-hat-tricks-one-team-one-season-playoff': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        {
            accessor: 'hatTricks',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Hat Tricks'),
        },
    ],

    // Assists
    'team-most-assists-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.POINTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.ASSISTS_COL,
    ],
    'fewest-assists-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.POINTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.ASSISTS_COL,
    ],
    'fewest-assists-one-season-70-game-min': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.POINTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.ASSISTS_COL,
    ],

    // Points
    'most-scoring-points-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        BaseColumnDefinitions.SCORING_POINTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
    ],
    'most-scoring-points-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.SCORING_POINTS_COL,
    ],
    'most-scoring-points-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORING_POINTS_COL,
        BaseColumnDefinitions.SCORE_COL,
        {
            accessor: 'homePoints',
            Header: CreateHeaderCell('Home Points'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingPoints',
            Header: CreateHeaderCell('Visiting Points'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],

    // Penalties
    'team-most-penalty-minutes-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
    ],
    'most-penalties-both-teams-one-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        ...PENALTY_COLS,
    ],
    'most-penalty-minutes-both-teams-one-period': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        ...PENALTY_COLS,
    ],
    'most-penalties-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
    ],
    'most-penalties-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PENALTIES_COL,
        {
            accessor: 'homePenalties',
            Header: CreateHeaderCell('Home Penalties'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingPenalties',
            Header: CreateHeaderCell('Visiting Penalties'),
            getProps: GlobalColumnFunctions.getProps,
        },
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
        {
            accessor: 'homePenaltyMinutes',
            Header: CreateHeaderCell('Home PIM'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingPenaltyMinutes',
            Header: CreateHeaderCell('Visiting PIM'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],
    'most-penalties-one-team-one-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.PENALTIES_COL,
    ],
    'most-penalty-minutes-one-team-one-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
    ],
    'most-penalty-minutes-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
    ],
    'most-penalty-minutes-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
        {
            accessor: 'homePenaltyMinutes',
            Header: CreateHeaderCell('Home Penalty Minutes', 'Home PIM'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingPenaltyMinutes',
            Header: CreateHeaderCell('Visiting Penalty Minutes', 'Visiting PIM'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],

    // Penalties - playoff
    'most-penalties-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...PENALTY_COLS],
    'most-penalty-minutes-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...PENALTY_COLS],
    'most-penalties-both-teams-one-period-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        ...PENALTY_COLS,
        TEAM_PENALTY_SPLIT_COL,
    ],
    'most-penalties-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...PENALTY_COLS,
        TEAM_PENALTY_SPLIT_COL,
    ],
    'most-penalty-minutes-both-teams-one-period-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        ...PENALTY_COLS,
        TEAM_PIM_SPLIT_COL,
    ],
    'most-penalty-minutes-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...PENALTY_COLS,
        TEAM_PIM_SPLIT_COL,
    ],
    'fewest-penalties-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        ...PENALTY_COLS,
        TEAM_PENALTY_SPLIT_COL,
    ],
    'fewest-penalties-one-team-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, ...PENALTY_COLS],
    'most-penalties-one-team-one-game-playoff': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
    ],
    'most-penalties-both-teams-one-game-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
        TEAM_PENALTY_SPLIT_COL,
    ],
    'most-penalty-minutes-both-teams-one-game-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
        TEAM_PIM_SPLIT_COL,
    ],
    'most-penalties-one-team-one-period-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.PENALTIES_COL,
    ],
    'most-penalty-minutes-one-team-one-period-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
    ],
    'most-penalty-minutes-one-team-one-game-playoff': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...PENALTY_COLS,
    ],

    // Shots
    'team-most-blocked-shots-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
        BaseColumnDefinitions.MISSED_SHOTS_COL,
    ],
    'most-blocked-shots-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
    ],
    'most-blocked-shots-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
        {
            accessor: 'homeBlockedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Home Blocked Shots', 'Home BkS'),
        },
        {
            accessor: 'visitingBlockedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Visiting Blocked Shots', 'Vis. BkS'),
        },
    ],
    'team-most-missed-shots-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
        BaseColumnDefinitions.MISSED_SHOTS_COL,
    ],
    'most-missed-shots-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.MISSED_SHOTS_COL,
    ],
    'most-shots-one-team-one-game': [...TEAM_OPP_SEASON_DATE_COLS, BaseColumnDefinitions.SCORE_COL, SHOTS_GENERAL],
    'most-shots-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SHOTS_GENERAL,
        VISITING_SHOTS_COL,
        HOME_SHOTS_COL,
    ],
    'most-missed-shots-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.MISSED_SHOTS_COL,
        {
            accessor: 'visitingMissedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Visiting Missed Shots'),
        },
        {
            accessor: 'homeMissedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Home Missed Shots'),
        },
    ],
    'most-shots-one-team-one-period': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
    ],
    'most-shots-both-teams-one-period': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
        VISITING_SHOTS_COL,
        HOME_SHOTS_COL,
    ],
    'most-shots-one-team-one-game-playoffs': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SHOTS_GENERAL,
    ],
    'most-shots-both-teams-one-game-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SHOTS_GENERAL,
        VISITING_SHOTS_COL,
        HOME_SHOTS_COL,
    ],
    'most-missed-shots-both-teams-one-game-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.MISSED_SHOTS_COL,
        {
            accessor: 'visitingMissedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Visiting Missed Shots'),
        },
        {
            accessor: 'homeMissedShots',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Home Missed Shots'),
        },
    ],
    'most-shots-one-team-one-period-playoffs': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
    ],
    'most-shots-both-teams-one-period-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
        VISITING_SHOTS_COL,
        HOME_SHOTS_COL,
    ],
    'most-shots-one-team-one-ot-period-playoffs': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
    ],
    'most-shots-both-teams-one-ot-period-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        SHOTS_GENERAL,
        VISITING_SHOTS_COL,
        HOME_SHOTS_COL,
    ],
    'most-shots-one-team-ot-playoffs': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SHOTS_OT_1,
        SHOTS_OT_2,
        SHOTS_OT_3,
        SHOTS_OT_4,
        SHOTS_OT_5,
        SHOTS_OT_TOTAL,
    ],
    'most-shots-both-teams-ot-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        {
            accessor: 'gameId',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Game'),
            Cell: ({ original }) => (
                <div>
                    <a href={`https://www.nhl.com/gamecenter/${original.gameId}`}>
                        {original.visitingTeam.fullName}
                        <div>at</div>
                        {original.homeTeam.fullName}
                    </a>
                </div>
            ),
        },
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        {
            accessor: 'visitingScore',
            Header: CreateHeaderCell('Score'),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 120,
            Cell: ({ original }) => (
                <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
                    {original.visitingTeam.triCode} {original.visitingScore} @ {original.homeTeam.triCode}{' '}
                    {original.homeScore}
                </a>
            ),
        },
        {
            accessor: 'period',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('OT Periods'),
            Cell: ({ value }) => value - 3,
        },
        createCell('totalShots', 'Shots', 'Total Overtime Shots'),
        createCell('visitingTeamShots', 'Vis. Shots', 'Visiting Team Overtime Shots'),
        createCell('homeTeamShots', 'Home. Shots', 'Home Team Overtime Shots'),
    ],

    // Faceoffs
    'highest-face-off-win-pctg-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...FACEOFFS],
    'team-highest-faceoff-win-pctg-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'most-face-offs-won-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...FACEOFFS],
    'most-face-offs-taken-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...FACEOFFS],
    'most-face-offs-taken-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'most-face-offs-won-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],

    // Giveaways
    'team-most-giveaways-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...GIVE_TAKEAWAYS],
    'most-giveaways-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GIVEAWAYS_COL,
    ],
    'most-giveaways-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.GIVEAWAYS_COL,
        {
            accessor: 'homeGiveaways',
            Header: CreateHeaderCell('Home Giveaways', 'Home GvA'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingGiveaways',
            Header: CreateHeaderCell('Visiting Giveaways', 'Vis. GvA'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],

    // Takeaways
    'team-most-takeaways-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...GIVE_TAKEAWAYS],
    'most-takeaways-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.TAKEAWAYS_COL,
    ],
    'most-takeaways-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.TAKEAWAYS_COL,
        {
            accessor: 'homeTakeaways',
            Header: CreateHeaderCell('Home Takeaways', 'Home Tka'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'visitingTakeaways',
            Header: CreateHeaderCell('Visiting Takeaways', 'Vis. Tka'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],

    // Shutouts
    'team-most-shutouts-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'most-games-shutout-by-opponent-one-season': [
        ...BaseColumnDefinitions.TEAM_SEASON_COLS,
        ...COMMON_COLS,
        {
            accessor: 'timesShutout',
            Header: CreateHeaderCell('Times Shutout', 'Times SO'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],
    'most-consecutive-games-shutout-by-opponent': LONGEST_COLS,
    'most-games-shutout-by-opponent-season-start': LONGEST_COLS,
    'most-consecutive-shutouts-one-season': LONGEST_COLS,
    'most-consecutive-shutouts-season-start': LONGEST_COLS,

    // Shutouts - playoffs
    'most-shutouts-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        GAMES_PLAYED_BY_ROUND,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'fewest-shutouts-all-teams-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        GAMES_PLAYED_BY_ROUND,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'most-shutouts-both-teams-one-series-playoff': [
        ...TEAM_SERIES_BOTH_TEAM_COLS,
        BaseColumnDefinitions.SHUTOUTS_COL,
        TEAM_SHUTOUTS_SPLIT_COL,
    ],

    // Hits
    'team-most-hits-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...HITS],
    'most-hits-one-team-one-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-hits-both-teams-one-game': [
        BaseColumnDefinitions.INDEX_COL,
        GAME_BOTH_TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.HITS_COL,
        HOME_HITS_COL,
        VISITING_HITS_COL,
    ],

    // Overtime - playoffs
    'most-overtime-games-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        {
            accessor: 'overtimeGames',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Games Played', 'OT GP'),
        },
        {
            accessor: 'overtimeGamesByRound',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Games by Round', 'OT GP by Round'),
        },
    ],
    'most-overtime-games-one-team-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.SEASON_COL,
        OT_GP_COL,
        {
            accessor: 'overtimeWins',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Wins', 'OT W'),
        },
        {
            accessor: 'overtimeLosses',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Losses', 'OT L'),
        },
        {
            accessor: 'overtimeTies',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Ties', 'OT T'),
        },
    ],
    'most-overtime-games-one-series-playoff': [...TEAM_SERIES_ONE_TEAM_COLS, OT_GP_COL],
    'fewest-overtime-games-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        STANLEY_CUP_WINNER,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        {
            accessor: 'overtimeGames',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Games', 'OT GP'),
        },
        {
            accessor: 'overtimeGamesByRound',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Overtime Games by Round', 'OT GP by Round'),
        },
    ],
    'shortest-overtime-playoff': TIMED_OT_COLS,
    'longest-overtime-playoff': TIMED_OT_COLS,
    'all-overtime-playoff': [
        {
            ...BaseColumnDefinitions.GAME_DATE_COL,
            Cell: ({ original }) => dateTimeFormatter.formatBirthDate(original.gameDate),
            sortType: (a, b) =>
                a.original.gameDate === b.original.gameDate
                    ? a.original.gameId - b.original.gameId
                    : a.original.gameDate.localeCompare(b.original.gameDate),
        },
        {
            accessor: 'seriesTitle',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: window.innerWidth > 414 ? 100 : 100,
            headerClassName: 'left',
            className: 'left',
            Header: CreateHeaderCell('Series Title', 'Series', { textAlign: 'left' }),
        },
        {
            accessor: 'seriesGameNumber',
            Cell: ({ value }) => value || '--',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 63,
            headerClassName: 'center',
            className: 'center',
            Header: CreateHeaderCell('Game Number', 'Game #', { textAlign: 'center' }),
        },
        {
            ...BaseColumnDefinitions.SCORE_COL,
            Cell: ({ original }) => (
                <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
                    {original.score.slice(0, original.score.length - 3)}
                </a>
            ),
        },
        {
            ...OT_PERIODS_COL,
            Cell: ({ original, value }) => {
                // Round Robin Playoff games can have a Shootout
                return isRoundRobinShootoutGame(original.gameId, original.seriesGameNumber, value)
                    ? 'SO'
                    : ordinalFormatter(value) + ' OT';
            },
            Header: 'OT Period',
        },
        {
            ...OT_SECONDS_COL,
            Cell: ({ original, value }) => {
                // Round Robin Playoff games can have a Shootout
                return isRoundRobinShootoutGame(original.gameId, original.seriesGameNumber, original.overtimePeriods)
                    ? '--'
                    : dateTimeFormatter.displaySecondsFormatted(value);
            },
        },
        {
            accessor: 'winningTeam',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: window.innerWidth > 414 ? 150 : 130,
            headerClassName: 'left',
            className: 'left',
            Header: CreateHeaderCell('Winning Team Name', 'W. Team', { textAlign: 'left' }),
            Cell: ({ original }) => {
                if (!original.winningTeam) {
                    return 'N/A';
                } else {
                    return original.winningTeam;
                }
            },
        },
        {
            accessor: 'losingTeam',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: window.innerWidth > 414 ? 150 : 130,
            headerClassName: 'left',
            className: 'left',
            Header: CreateHeaderCell('Losing Team Name', 'L. Team', { textAlign: 'left' }),
            Cell: ({ original }) => {
                if (!original.losingTeam) {
                    return 'N/A';
                } else {
                    return original.losingTeam;
                }
            },
        },
        GWG_COL,
        {
            accessor: 'winningGoalieLastName',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: window.innerWidth > 414 ? 150 : 130,
            headerClassName: 'left',
            className: 'left',
            Header: CreateHeaderCell('Winning Goalie', 'W. Goaltender', { textAlign: 'left' }),
            Cell: ({ original }) => {
                if (!original.winningGoaliePlayerId) {
                    return 'N/A';
                }

                const playerObj = {
                    playerId: original.winningGoaliePlayerId,
                    firstName: original.winningGoalieFirstName,
                    lastName: original.winningGoalieLastName,
                };

                return <PlayerLink player={playerObj} />;
            },
        },
        {
            accessor: 'losingGoalieLastName',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: window.innerWidth > 414 ? 150 : 130,
            headerClassName: 'left',
            className: 'left',
            Header: CreateHeaderCell('Losing Goalie', 'L. Goaltender', { textAlign: 'left' }),
            Cell: ({ original }) => {
                if (!original.losingGoaliePlayerId) {
                    return 'N/A';
                }

                const playerObj = {
                    playerId: original.losingGoaliePlayerId,
                    firstName: original.losingGoalieFirstName,
                    lastName: original.losingGoalieLastName,
                };

                return <PlayerLink player={playerObj} />;
            },
        },
    ],

    // Shootout
    'most-shootout-wins-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'team-most-shootout-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'most-shootout-shots-taken-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'most-shootout-shots-taken-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'most-shootout-road-wins-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME_ROAD, ...SHOOTOUT_ROAD],
    'most-shootout-road-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_ROAD_COLS, ...SHOOTOUT_ROAD],
    'most-shootout-home-wins-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME_HOME, ...SHOOTOUT_HOME],
    'most-shootout-home-wins-one-season': [...BaseColumnDefinitions.TEAM_SEASON_HOME_COLS, ...SHOOTOUT_HOME],
    'most-shootout-goals-scored-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'most-shootout-games-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'fewest-shootout-goals-against-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'best-shootout-winning-pctg-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'best-shootout-scoring-pctg-all-time': [...BaseColumnDefinitions.TEAM_ALL_TIME, ...SHOOTOUT],
    'most-shootout-goals-scored-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'most-shootout-games-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'fewest-shootout-goals-against-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'best-shootout-winning-pctg-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],
    'best-shootout-scoring-pctg-one-season': [...BaseColumnDefinitions.TEAM_SEASON_COLS, ...SHOOTOUT],

    // Streaks
    'longest-home-losing-streak-season-start': LONGEST_COLS,
    'longest-home-losing-streak-one-season': LONGEST_COLS,
    'longest-home-undefeated-streak-season-start': LONGEST_COLS,
    'longest-home-undefeated-streak-one-season': LONGEST_COLS,
    'longest-home-win-streak-one-season': LONGEST_COLS,
    'longest-home-winless-streak-one-season': LONGEST_COLS,
    'longest-home-winless-streak-season-start': LONGEST_COLS,
    'longest-home-win-streak-season-start': LONGEST_COLS,
    'longest-losing-streak-season-start': LONGEST_COLS,
    'longest-losing-streak-one-season': LONGEST_COLS,
    'longest-road-losing-streak-season-start': LONGEST_COLS,
    'longest-road-losing-streak-one-season': LONGEST_COLS,
    'longest-road-undefeated-streak-season-start': LONGEST_COLS,
    'longest-road-undefeated-streak-one-season': LONGEST_COLS,
    'longest-road-winless-streak-season-start': LONGEST_COLS,
    'longest-road-winless-streak-one-season': LONGEST_COLS,
    'longest-road-win-streak-season-start': LONGEST_COLS,
    'longest-road-win-streak-one-season': LONGEST_COLS,
    'team-longest-undefeated-streak-one-season': LONGEST_COLS,
    'longest-undefeated-streak-season-start': LONGEST_COLS,
    'longest-win-streak-one-season': LONGEST_COLS,
    'longest-winless-streak-one-season': LONGEST_COLS,
    'longest-winless-streak-season-start': LONGEST_COLS,
    'longest-win-streak-season-start': LONGEST_COLS,
    'longest-non-shutout-streak': LONGEST_COLS.filter((col) => col.accessor !== 'seasonId'),
    'longest-non-shutout-streak-including-playoffs': LONGEST_COLS.filter((col) => col.accessor !== 'seasonId'),
    'longest-winning-streaks-vs-franchise': FRANCHISE_LONGEST_COLS,
    'longest-winning-streaks-vs-franchise-active': FRANCHISE_LONGEST_COLS,
    'longest-home-winning-streaks-vs-franchise': FRANCHISE_LONGEST_COLS,
    'longest-home-winning-streaks-vs-franchise-active': FRANCHISE_LONGEST_COLS,
    'longest-road-winning-streaks-vs-franchise': FRANCHISE_LONGEST_COLS,
    'longest-road-winning-streaks-vs-franchise-active': FRANCHISE_LONGEST_COLS,

    // Streaks - playoffs
    'longest-home-undefeated-streak-including-playoffs': LONGEST_COLS,
    'longest-home-win-streak-including-playoffs': LONGEST_COLS,
    'longest-road-undefeated-streak-including-playoffs': LONGEST_COLS,
    'longest-road-win-streak-including-playoffs': LONGEST_COLS,
    'longest-undefeated-streak-including-playoffs': LONGEST_COLS,
    'longest-win-streak-including-playoffs': LONGEST_COLS,

    // Winning Streak - playoffs
    'longest-win-streak-multiple-seasons-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        {
            ...BaseColumnDefinitions.WIN_STREAK_COL,
            accessor: 'streak',
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-win-streak-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        {
            accessor: 'opponents',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Opponents'),
            Cell: ({ value }) => {
                if (!value) {
                    return;
                }
                const teams = value.split(',');
                return (
                    <div>
                        {teams.map((team, index) =>
                            index === teams.length - 1 ? <div key={team}>{team}</div> : <div key={team}>{team},</div>
                        )}
                    </div>
                );
            },
        },
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Losing Streak - playoffs
    'longest-loss-streak-multiple-seasons-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        {
            accessor: 'streak',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Loss Streak'),
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Playoff Appearances
    'most-cup-games-played-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        BaseColumnDefinitions.PLAYOFF_SEASONS_COL,
        STANLEY_CUP_APPEARANCES,
        STANLEY_CUP_WINS,
    ],
    'most-consecutive-cup-games-played-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        {
            accessor: 'consecutiveStanleyCupFinalSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Stanley Cup Final Seasons', 'Consecutive Seasons'),
        },
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
    ],
    'most-cup-wins-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        BaseColumnDefinitions.PLAYOFF_SEASONS_COL,
        STANLEY_CUP_APPEARANCES,
        STANLEY_CUP_WINS,
    ],
    'most-consecutive-cup-wins-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        {
            accessor: 'consecutiveStanleyCupChampSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Stanley Cup Champ Seasons', 'Consecutive Seasons'),
        },
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
    ],
    'team-most-series-played': [
        BaseColumnDefinitions.INDEX_COL,
        createCell('teamName', 'Franchise'),
        createCell('playoffSeasons', 'Playoff Appearances'),
        createCell('seriesPlayed', 'Series Played'),
        createCell('seriesWins', 'Series Wins'),
        createCell('seriesLosses', 'Series Losses'),
        {
            Header: 'Series Win %',
            accessor: 'seriesWinPctg',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => statFormatter.displayValueAs('winPctg')(value),
            sortDescFirst: true,
            sortType: 'basic',
        },
    ],
    'team-most-series-won': [
        BaseColumnDefinitions.INDEX_COL,
        createCell('teamName', 'Franchise'),
        createCell('playoffSeasons', 'Playoff Appearances'),
        createCell('seriesPlayed', 'Series Played'),
        createCell('seriesWins', 'Series Wins'),
        createCell('seriesLosses', 'Series Losses'),
        {
            Header: 'Series Win %',
            accessor: 'seriesWinPctg',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => statFormatter.displayValueAs('winPctg')(value),
            sortDescFirst: true,
            sortType: 'basic',
        },
    ],
    'team-most-consecutive-series-won': [
        BaseColumnDefinitions.INDEX_COL,
        createCell('teamName', 'Franchise'),
        createCell('winStreak', 'Consecutive Series Won'),
        {
            Header: 'List of Series',
            accessor: 'summary',
            getProps: GlobalColumnFunctions.getProps,
            className: 'seasons',
            Cell: ({ value }) => {
                return value.split(', ').map((series, idx) => <div key={idx}>{series}</div>);
            },
        },
    ],
    'most-seasons-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        BaseColumnDefinitions.PLAYOFF_SEASONS_COL,
        STANLEY_CUP_APPEARANCES,
        STANLEY_CUP_WINS,
    ],
    'team-most-consecutive-seasons-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        FRANCHISE_COL,
        {
            accessor: 'consecutivePlayoffSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Stanley Cup Champ Seasons', 'Consecutive Seasons'),
        },
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
    ],

    // Fastest
    'fastest-6-goals-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_FIVE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
        GOAL_6_COL,
    ],
    'fastest-5-goals-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
    ],
    'fastest-5-goals-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
    ],
    'fastest-4-goals-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
    ],
    'fastest-4-goals-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
    ],
    'fastest-3-goals-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-3-goals-from-period-start-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-3-goals-from-period-start-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-3-goals-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-2-goals-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-2-goals-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-2-goals-from-game-start-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        {
            ...BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
            Header: CreateHeaderCell('Seconds Since Start of Game', 'Seconds'),
        },
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-2-goals-from-period-start-both-teams': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-2-goals-from-period-start-one-team': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],

    // Fastest - playoff
    'fastest-5-goals-both-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-5-goals-one-team-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-4-goals-both-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-4-goals-one-team-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-3-goals-both-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-3-goals-one-team-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-2-goals-both-teams-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        ALT_MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-2-goals-one-team-playoff': [
        ...TEAM_SEASON_DATE_COLS,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'team-performance-any-calendar-month': [
        ...BaseColumnDefinitions.TEAM_ANY_MONTH_COLS,
        {
            Header: CreateHeaderCell('Most Points'),
            accessor: 'mostPoints',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Points Month'),
            accessor: 'mostPointsMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most Wins'),
            accessor: 'mostWins',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Wins Month'),
            accessor: 'mostWinsMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most Losses'),
            accessor: 'mostLosses',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Losses Month'),
            accessor: 'mostLossesMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most Ties'),
            accessor: 'mostTies',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Ties Month'),
            accessor: 'mostTiesMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most OT Losses'),
            accessor: 'mostOtLosses',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most OT Losses Month'),
            accessor: 'mostOtLossesMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most Goals For'),
            accessor: 'mostGoalsFor',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Goals For Month'),
            accessor: 'mostGoalsForMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
        {
            Header: CreateHeaderCell('Most Goals Against'),
            accessor: 'mostGoalsAgainst',
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 60,
            sortDescFirst: true,
        },
        {
            Header: CreateHeaderCell('Most Goals Against Month'),
            accessor: 'mostGoalsAgainstMonth',
            className: 'month-string',
            Cell: ({ value }) => formatMonthDisplay(value),
            getProps: GlobalColumnFunctions.getProps,
            minWidth: 80,
        },
    ],
    'most-points-october': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-november': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-december': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-january': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-february': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-march': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-points-april': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-october': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-november': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-december': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-january': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-february': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-march': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-wins-april': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-october': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-november': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-december': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-january': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-february': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-march': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],
    'most-losses-april': [...BaseColumnDefinitions.TEAM_MONTH_COLS, ...getMonthBaseColumns(COMMON_COLS)],

    // Outdoor columns
    'most-outdoor-games-played-team': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        {
            ...BaseColumnDefinitions.OVERTIME_LOSSES_COL,
            accessor: 'otLosses',
        },
        BaseColumnDefinitions.POINTS_COL,
        POINT_PCTG_COL,
        BaseColumnDefinitions.GOALS_FOR_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],
    'most-outdoor-wins-team': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        {
            ...BaseColumnDefinitions.OVERTIME_LOSSES_COL,
            accessor: 'otLosses',
        },
        BaseColumnDefinitions.POINTS_COL,
        POINT_PCTG_COL,
        BaseColumnDefinitions.GOALS_FOR_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],
    'most-outdoor-goals-one-team-game-team': [...OUTDOOR_GAME_COLS, BaseColumnDefinitions.GOALS_COL],
    'most-outdoor-goals-one-team-period-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-outdoor-goals-both-teams-game-team': [...OUTDOOR_DATE_SCORE_COLS, BaseColumnDefinitions.GOALS_COL],
    'most-outdoor-goals-both-teams-period-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-outdoor-power-play-goals-one-team-game-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-outdoor-power-play-goals-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_PPG_COL,
        ROAD_PPG_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-outdoor-shorthanded-goals-one-team-game-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'most-outdoor-shorthanded-goals-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_SHG_COL,
        ROAD_SHG_COL,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'fastest-two-outdoor-goals-one-team-team': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.SCORE_COL,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-two-goals-one-team-from-start-of-outdoor-game-team': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-two-goals-one-team-from-start-of-any-outdoor-period-team': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
        BaseColumnDefinitions.PERIOD_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'most-outdoor-shots-on-goal-one-team-game-team': [...OUTDOOR_GAME_COLS, BaseColumnDefinitions.SHOTS_COL],
    'most-outdoor-shots-on-goal-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_SHOTS_COL,
        ROAD_SHOTS_COL,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-outdoor-shots-on-goal-one-team-period-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-outdoor-shots-on-goal-both-teams-period-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        HOME_SHOTS_COL,
        ROAD_SHOTS_COL,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-outdoor-hits-one-team-game-team': [...OUTDOOR_GAME_COLS, BaseColumnDefinitions.HITS_COL],
    'most-outdoor-hits-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_HITS_COL,
        ROAD_HITS_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-outdoor-hits-one-team-period-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-outdoor-hits-both-teams-period-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        HOME_HITS_COL,
        ROAD_HITS_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-outdoor-blocked-shots-one-team-game-team': [...OUTDOOR_GAME_COLS, BaseColumnDefinitions.BLOCKS_COL],
    'most-outdoor-blocked-shots-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_BLOCKS_COL,
        ROAD_BLOCKS_COL,
        BaseColumnDefinitions.BLOCKS_COL,
    ],
    'most-outdoor-blocked-shots-one-team-period-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.BLOCKS_COL,
    ],
    'most-outdoor-blocked-shots-both-teams-period-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        HOME_BLOCKS_COL,
        ROAD_BLOCKS_COL,
        BaseColumnDefinitions.BLOCKS_COL,
    ],
    'most-outdoor-penalty-minutes-one-team-game-team': [
        ...OUTDOOR_GAME_COLS,
        BaseColumnDefinitions.PENALTIES_COL,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
    ],
    'most-outdoor-penalty-minutes-both-teams-game-team': [
        ...OUTDOOR_DATE_SCORE_COLS,
        HOME_PENALTY_MINUTES_COL,
        ROAD_PENALTY_MINUTES_COL,
        BaseColumnDefinitions.PENALTY_MINUTES_COL,
    ],
    'series-situational-records': SERIES_SITUATION_COLS,
    'franchise-series-situational-records': SERIES_SITUATION_COLS,
    'playoff-franchise-game-by-game-records': [
        {
            Header: CreateHeaderCell('Game #'),
            accessor: 'gameNumber',
            getProps: GlobalColumnFunctions.getProps,
        },
        SERIES_GAME_RECORD,
        SERIES_HOME_GAME_RECORD,
        SERIES_ROAD_GAME_RECORD,
    ],
    'team-elimination-game-record': ELIMINATION_GAMES_COLS,
    'team-most-games-facing-elimination-season': ELIMINATION_GAMES_SEASON_COLS,
    'team-most-wins-facing-elimination-season': ELIMINATION_GAMES_SEASON_COLS,
    'team-most-games-facing-elimination-series': ELIMINATION_GAMES_SERIES_COLS,
    'team-most-wins-facing-elimination-series': ELIMINATION_GAMES_SERIES_COLS,
    'team-most-goals-facing-elimination-game': [
        ...ELIMINATION_GAME_COLS,
        ...GOALS_FOR_GOALS_AGAINST_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
    ],
    'team-most-goals-against-facing-elimination-game': [
        ...ELIMINATION_GAME_COLS,
        ...GOALS_FOR_GOALS_AGAINST_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
    ],
    'elimination-game-shutouts': [
        ...ELIMINATION_GAME_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
        RESULT_COL,
        GOALIE_COL,
    ],
    'team-potential-clinching-game-record': ELIMINATION_GAMES_COLS,
    'team-most-potential-clinching-games-season': ELIMINATION_GAMES_SEASON_COLS,
    'team-most-potential-clinching-games-series': ELIMINATION_GAMES_SERIES_COLS,
    'team-most-goals-potential-clinching-game': [
        ...ELIMINATION_GAME_COLS,
        ...GOALS_FOR_GOALS_AGAINST_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
    ],
    'team-most-goals-against-potential-clinching-game': [
        ...ELIMINATION_GAME_COLS,
        ...GOALS_FOR_GOALS_AGAINST_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
    ],
    'potential-clinching-game-shutouts': [
        ...ELIMINATION_GAME_COLS,
        BaseColumnDefinitions.GAME_SCORE_COL,
        RESULT_COL,
        GOALIE_COL,
    ],
    'most-stanley-cups': SCF_COLS,
    'most-consecutive-stanley-cups': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        {
            accessor: 'consecutiveStanleyCupChampSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Stanley Cup Champ Seasons', 'Consecutive Stanley Cups'),
        },
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
    ],
    'most-stanley-cup-final-appearances': SCF_COLS,
    'most-consecutive-scf-appearances': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.TEAM_COL,
        SCF_CONSEC_APPEARANCES_COL,
        SCF_SERIES_RECORD_COL,
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
    ],
    'scf-series-situational-records': SERIES_SITUATION_COLS,
    'most-goals-one-team-scf-series': [
        ...TEAM_OPP_SEASON_COLS,
        SCF_RESULT_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-one-team-scf-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-one-team-scf-period': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-both-teams-scf-series': [
        ...SCF_SERIES_BOTH_TEAMS_BASE_COLS,
        BaseColumnDefinitions.GOALS_COL,
        { ...TEAM_GOALS_SPLIT_COL, Header: 'Breakdown' },
    ],
    'most-goals-both-teams-scf-game': [...SCF_BOTH_TEAMS_GOALS_BASE, BaseColumnDefinitions.GOALS_COL],
    'most-goals-both-teams-scf-period': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        { ...TEAM_GOALS_SPLIT_COL, Header: 'Breakdown' },
    ],
    'fastest-two-goals-one-team-scf-game': [
        ...TEAM_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-three-goals-one-team-scf-game': [
        ...TEAM_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-four-goals-one-team-scf-game': [
        ...TEAM_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
    ],
    'fastest-five-goals-one-team-scf-game': [
        ...TEAM_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
    ],
    'fastest-two-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        SECONDS_SINCE_LAST_GOAL_COL,
        GOAL_1_COL,
        GOAL_2_COL,
    ],
    'fastest-three-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        SECONDS_SINCE_LAST_TWO_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
    ],
    'fastest-four-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        SECONDS_SINCE_LAST_THREE_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
    ],
    'fastest-five-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        SECONDS_SINCE_LAST_FOUR_GOALS_COL,
        GOAL_1_COL,
        GOAL_2_COL,
        GOAL_3_COL,
        GOAL_4_COL,
        GOAL_5_COL,
    ],
    'most-power-play-goals-one-team-scf-series': [
        ...TEAM_OPP_SEASON_COLS,
        SCF_RESULT_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-power-play-goals-one-team-scf-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-power-play-goals-one-team-scf-period': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-power-play-goals-both-teams-scf-series': [
        ...SCF_SERIES_BOTH_TEAMS_BASE_COLS,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
        { ...TEAM_PPG_SPLIT_COL, Header: 'Breakdown' },
    ],
    'most-power-play-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
        { ...TEAM_PPG_SPLIT_COL, Header: 'Breakdown' },
    ],
    'most-power-play-goals-both-teams-scf-period': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
        { ...TEAM_PPG_SPLIT_COL, Header: 'Breakdown' },
    ],
    'most-shorthanded-goals-one-team-scf-series': [
        ...TEAM_OPP_SEASON_COLS,
        SCF_RESULT_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'most-shorthanded-goals-one-team-scf-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'most-shorthanded-goals-both-teams-scf-series': [
        ...SCF_SERIES_BOTH_TEAMS_BASE_COLS,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
        { ...TEAM_SHG_SPLIT_COL, Header: 'Breakdown' },
    ],
    'most-shorthanded-goals-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
        { ...TEAM_SHG_SPLIT_COL, Header: 'Breakdown' },
    ],
    'all-time-overtime-games-scf-history': [
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        ...SCF_GAME_NUM_RESULT_COLS,
        OT_PERIODS_COL,
        OT_SECONDS_COL,
        GWG_COL,
    ],
    'shortest-overtimes-scf-history': [
        BaseColumnDefinitions.INDEX_COL,
        { ...ALT_MATCHUP_COL, Header: 'Matchup' },
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        ...SCF_GAME_NUM_RESULT_COLS,
        OT_SECONDS_COL,
        GWG_COL,
    ],
    'longest-overtimes-scf-history': [
        BaseColumnDefinitions.INDEX_COL,
        { ...ALT_MATCHUP_COL, Header: 'Matchup' },
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        ...SCF_GAME_NUM_RESULT_COLS,
        OT_PERIODS_COL,
        OT_SECONDS_COL,
        GWG_COL,
    ],
    'most-overtime-games-scf-series': [
        BaseColumnDefinitions.INDEX_COL,
        MATCHUP_COL,
        BaseColumnDefinitions.SEASON_COL,
        RESULT_COL,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, accessor: 'seriesLength', Header: 'Total Games' },
        OT_GP_COL,
    ],
    'most-shots-on-goal-one-team-scf-game': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-shots-on-goal-one-team-scf-period': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-shots-on-goal-one-team-scf-overtime-period': [
        ...TEAM_OPP_SEASON_DATE_COLS,
        ...SCF_GAME_NUM_RESULT_COLS,
        BaseColumnDefinitions.OT_PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
    ],
    'most-shots-on-goal-both-teams-scf-game': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.SHOTS_COL,
        TEAM_SHOTS_SPLIT_COL,
    ],
    'most-shots-on-goal-both-teams-scf-period': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
        TEAM_SHOTS_SPLIT_COL,
    ],
    'most-shots-on-goal-both-teams-scf-overtime-period': [
        ...SCF_BOTH_TEAMS_GOALS_BASE,
        BaseColumnDefinitions.OT_PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
        TEAM_SHOTS_SPLIT_COL,
    ],
    'most-shutouts-one-teams-scf-series': [
        ...TEAM_OPP_SEASON_COLS,
        SCF_RESULT_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'most-shutouts-both-teams-scf-series': [
        ...SCF_SERIES_BOTH_TEAMS_BASE_COLS,
        BaseColumnDefinitions.SHUTOUTS_COL,
        { ...TEAM_SHUTOUTS_SPLIT_COL, Header: 'Breakdown' },
    ],
    ...teamComebackWinsColumnDefinitions,
    ...teamPointStreakColumnDefinitions,
    ...teamInternationalColumns,
};
